import React, { useState, useEffect } from 'react';
import { db, getCollectionName } from './firebase';
import { collection, getDocs, deleteDoc, doc } from './firestore-compat';

const EVENTOS_DESATIVADO = true;

const AdminEventos = () => {
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');

    const fetchAllEvents = async () => {
        if (EVENTOS_DESATIVADO) { setLoading(false); return; }
        setLoading(true);
        try {
            const collectionsToFetch = [
                { id: 'agendaDirecao', label: 'Agenda Direção' },
                { id: 'auditorias', label: 'Agenda Qualidade' },
                { id: 'agenda_pessoal', label: 'Agenda Pessoal' }
            ];

            let allEvents = [];

            for (const coll of collectionsToFetch) {
                const collName = getCollectionName(coll.id);
                try {
                    const snap = await getDocs(collection(db, collName));
                    snap.docs.forEach(d => {
                        allEvents.push({
                            id: d.id,
                            collectionType: coll.label,
                            collectionId: collName,
                            ...d.data()
                        });
                    });
                } catch (err) {
                    console.error(`Erro ao buscar ${collName}:`, err);
                }
            }

            // Ordenar por data (mais recentes primeiro para fácil acesso, ou mais antigos)
            allEvents.sort((a, b) => {
                const dateA = a.data ? new Date(a.data) : new Date(0);
                const dateB = b.data ? new Date(b.data) : new Date(0);
                return dateB - dateA; // Descending
            });

            setEventos(allEvents);
        } catch (error) {
            console.error("Erro geral na busca de eventos:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllEvents();
    }, []);

    const handleDelete = async (evento) => {
        if (!window.confirm(`Tem certeza ABSOLUTA que deseja EXCLUIR DEFINITIVAMENTE o evento:\n\nColeção: ${evento.collectionId}\nID: ${evento.id}\nDescrição: ${evento.descricao || evento.auditoria || 'Sem Nome'}?`)) {
            return;
        }

        try {
            await deleteDoc(doc(db, evento.collectionId, evento.id));
            alert('Evento excluído do banco de dados com sucesso.');
            // Atualizar lista
            setEventos(prev => prev.filter(e => !(e.id === evento.id && e.collectionId === evento.collectionId)));
        } catch (error) {
            console.error("Erro ao excluir evento:", error);
            alert("Erro ao excluir: " + error.message);
        }
    };

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [collectionFilter, setCollectionFilter] = useState('');
    const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

    const confirmDeepClean = async (originalId) => {
        if (!window.confirm("Executar o Deep Clean vai varrer o banco de dados e excluir TODOS os eventos (cópias) em todas as agendas que tenham ligação com este ID. Tem certeza?")) return;
        try {
            let count = 0;
            alert("Por enquanto, exclua linha por linha para ter certeza do ID exato.");
        } catch (e) { }
    }

    // Identificar duplicados SOMENTE DENTRO DA MESMA COLEÇÃO
    const duplicateMap = React.useMemo(() => {
        const groups = {};
        eventos.forEach(e => {
            const cleanDate = (e.data || 'nodate').trim();
            const cleanTime = (e.horario || 'notime').trim();
            // Limpa tudo que não for letra ou numero para garantir match MESMO com espaços extras ou hifens
            let text = (e.descricao || e.auditoria || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            text = text.substring(0, 25); // Pega só o começo para garantir match mesmo se o final for diferente

            const key = `${e.collectionId}_${cleanDate}_${cleanTime}_${text}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(e);
        });
        const dups = new Set();
        Object.values(groups).forEach(group => {
            if (group.length > 1) {
                group.forEach(e => dups.add(e.collectionId + '_' + e.id));
            }
        });
        return dups;
    }, [eventos]);

    const filteredEvents = eventos.filter(evento => {
        const str = filter.toLowerCase();
        const matchesText = (
            (evento.descricao || '').toLowerCase().includes(str) ||
            (evento.auditoria || '').toLowerCase().includes(str) ||
            (evento.id || '').toLowerCase().includes(str) ||
            (evento.originalEventId || '').toLowerCase().includes(str) ||
            (evento.sourceRef || '').toLowerCase().includes(str)
        );

        let matchesDate = true;
        if (startDate || endDate) {
            const evDateStr = evento.data;
            if (evDateStr) {
                if (startDate && evDateStr < startDate) matchesDate = false;
                if (endDate && evDateStr > endDate) matchesDate = false;
            } else {
                matchesDate = (!startDate && !endDate);
            }
        }

        let matchesCollection = true;
        if (collectionFilter) {
            matchesCollection = evento.collectionType === collectionFilter;
        }

        let matchesDuplicates = true;
        if (showDuplicatesOnly) {
            matchesDuplicates = duplicateMap.has(evento.collectionId + '_' + evento.id);
        }

        return matchesText && matchesDate && matchesCollection && matchesDuplicates;
    });

    if (loading) return <div className="p-4" style={{ textAlign: 'center', marginTop: '50px' }}>Carregando banco de eventos completo... <br /><small>(Isso pode demorar dependendo da quantidade de dados)</small></div>;

    if (EVENTOS_DESATIVADO) {
        return (
            <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 54, marginBottom: 16 }}>🛠️</div>
                <h3 style={{ color: '#1e293b', marginBottom: 8, fontWeight: 700, fontSize: '1.2rem' }}>Gestão de Eventos temporariamente desativada</h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Em manutenção. Volta em breve.</p>
            </div>
        );
    }

    return (
        <div className="audit-container" style={{ padding: '20px' }}>
            <h2>Painel do Super Admin (Banco de Dados de Eventos)</h2>
            <p>Atenção: A exclusão aqui é feita diretamente no banco de dados e ignora qualquer regra de aplicativo.</p>

            <div className="controls" style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder="Filtrar por Descrição, ID ou sourceRef..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{ padding: '8px', width: '350px' }}
                />
                <select
                    value={collectionFilter}
                    onChange={(e) => setCollectionFilter(e.target.value)}
                    style={{ padding: '8px', minWidth: '150px' }}
                >
                    <option value="">Todas as Tabelas</option>
                    <option value="Agenda Direção">Agenda Direção</option>
                    <option value="Agenda Qualidade">Agenda Qualidade</option>
                    <option value="Agenda Pessoal">Agenda Pessoal</option>
                </select>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                    <label style={{ fontSize: '13px', fontWeight: 'bold' }}>De:</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{ padding: '6px' }}
                    />
                    <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Até:</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{ padding: '6px' }}
                    />
                </div>
                <button
                    onClick={fetchAllEvents}
                    style={{ padding: '8px 15px', backgroundColor: '#00897b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                    Atualizar Tabela
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', backgroundColor: '#fff3cd', padding: '5px 10px', borderRadius: '4px', border: '1px solid #ffeeba' }}>
                    <input
                        type="checkbox"
                        checked={showDuplicatesOnly}
                        onChange={(e) => setShowDuplicatesOnly(e.target.checked)}
                    />
                    <strong>Mostrar Apenas Possíveis Duplicados</strong>
                </label>
                <span style={{ margin: 'auto 0' }}>Exibindo {filteredEvents.length} de {eventos.length} registros no banco</span>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: '75vh', border: '1px solid #ddd' }}>
                <table className="audit-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr style={{ backgroundColor: '#343a40', color: 'white', textAlign: 'left' }}>
                            <th style={{ padding: '10px' }}>Data/Hora</th>
                            <th style={{ padding: '10px' }}>Coleção (Tabela)</th>
                            <th style={{ padding: '10px' }}>Descrição do Evento</th>
                            <th style={{ padding: '10px' }}>Status</th>
                            <th style={{ padding: '10px' }}>ID Original (Raiz)</th>
                            <th style={{ padding: '10px' }}>Ligação Externa (Source)</th>
                            <th style={{ padding: '10px' }}>Ações de Risco</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEvents.map(evento => {
                            // Destacando visualmente se for na agenda pessoal mas tem ligação externa
                            const isCopy = evento.collectionId.includes('agenda_pessoal') && (evento.originalEventId || evento.id.startsWith('ext-'));
                            const isDuplicate = duplicateMap.has(evento.collectionId + '_' + evento.id);

                            let bgColor = 'transparent';
                            if (isDuplicate) bgColor = '#ffeeba'; // Amarelo forte para duplicados detectados
                            else if (isCopy) bgColor = '#fff3cd'; // Amarelo claro para cópias normais

                            return (
                                <tr key={evento.id + evento.collectionId} style={{ borderBottom: '1px solid #ccc', backgroundColor: bgColor }}>
                                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                                        <strong>{evento.data ? evento.data.split('-').reverse().join('/') : 'S/D'}</strong><br />
                                        {evento.horario}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', padding: '4px 8px', borderRadius: '4px' }}>
                                            {evento.collectionType}
                                        </span><br />
                                        <small style={{ color: '#888' }}>{'id: ' + evento.collectionId}</small>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <strong style={{ fontSize: '14px', color: '#00897b' }}>{evento.descricao || evento.auditoria || '(Sem descrição)'}</strong>
                                        {isDuplicate && <span style={{ marginLeft: '8px', backgroundColor: '#dc3545', color: 'white', padding: '2px 5px', borderRadius: '3px', fontSize: '10px', fontWeight: 'bold' }}>DUPLICADO</span>}
                                        <br />
                                        <span style={{ color: '#666' }}>Criador UID: {evento.createdBy || evento.criadoPorUid || 'N/A'}</span>
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <span style={{
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            backgroundColor: evento.status === 'Feito' ? '#d4edda' : '#f8f9fa'
                                        }}>
                                            {evento.status || 'N/A'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px', fontFamily: 'monospace', color: '#dc3545' }}>
                                        <div style={{ fontWeight: 'bold' }}>{evento.id}</div>
                                    </td>
                                    <td style={{ padding: '8px', fontFamily: 'monospace', color: '#e67e22', fontSize: '11px' }}>
                                        {evento.originalEventId && <div>Orig: {evento.originalEventId}</div>}
                                        {evento.sourceRef && <div>Source: {evento.sourceRef}</div>}
                                        {evento.isExternalCopy && <div>[Cópia Externa]</div>}
                                        {!evento.originalEventId && !evento.sourceRef && !evento.isExternalCopy && <span style={{ color: '#999' }}>-</span>}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <button
                                            onClick={() => handleDelete(evento)}
                                            style={{
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                padding: '5px 10px',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Deletar Definitivo
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredEvents.length === 0 && <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Nenhum evento encontrado.</div>}
            </div>
        </div>
    );
};

export default AdminEventos;
